import { ChangeDetectorRef, inject, Injectable } from "@angular/core";
import {
  PermissionContext,
  ROLES,
} from "../../../../../models/enumeration-list.model";
import { DataService, ObjectKeys } from "../../../../../services/data.service";
import { ApprovalRequestService } from "../approval-request.service";
import {
  ApprovalProcessLib,
  ApprovalRequest,
  ApprovalStep,
  NotificationMessage,
  ToastType,
} from "@barthauer/custom/xmt-models";
import { PersistMode, TargetColumnValue } from "@barthauer/abc/core/data";
import { DataService as CoreDataService } from "@barthauer/abc/core/data";
import {
  ActionIcon,
  RequestFormattedStatus,
  RequestStatus,
  StepDecision,
} from "../enums";
import {
  catchError,
  concatMap,
  delay,
  EMPTY,
  first,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from "rxjs";
import { ApprovalStepViewModel } from "../../../../../view-models/approvalStep.view-model";
import { PersonViewModel } from "libs/custom/xmt/models/src/lib/viewModels/person.viewModel";
import { NotificationService } from "../../../../../services/notification.service";
import {
  NotificationService1,
  NotificationSettingType,
  EmailNotificationSubjects,
  EmailNotificationDescriptions,
  EmailNotificationType,
} from "../../../../../services/notification1.service";
import { MeasureService } from "../../../measure.service";
import { formatMessage } from "devextreme/localization";
import { AIMSROUTES } from "../../../../../../app-routing.module";
import { AppService } from "../../../../../services/app.service";
import { LanguageService } from "../../../../../services/language.service";
import { Permission } from "../../../../../user-right/enums";
export interface IPersistResult {
  Id: string;
  ObjectType: string;
}
@Injectable()
export class RequestPopUpService {
  private readonly dataService = inject(DataService);
  private readonly approvalRequestService = inject(ApprovalRequestService);
  private readonly coreDataService = inject(CoreDataService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly notificationService = inject(NotificationService);
  private readonly notificationService1 = inject(NotificationService1);
  private readonly measureService = inject(MeasureService);
  private readonly appService = inject(AppService);
  private readonly languageService = inject(LanguageService);

  private readonly approvalProcessLibDesignation =
    "Change Maturity Level Process";
  private adminWithdraw = false;

  public setSubmitButtonText(permission: string): string {
    switch (permission) {
      case PermissionContext.MeasureChangeHartegrad:
        return this.dataService.res(
          `Ubt-MeasureDetail-Approval-Request-Modify`,
        );
      case PermissionContext.MeasureApprovalRequestStep1:
      case PermissionContext.MeasureApprovalRequestStep2:
        return this.dataService.res(
          `Ubt-MeasureDetail-Approval-Request-Approve`,
        );
      case PermissionContext.MeasureCreateApprovalRequest:
        return this.dataService.res(
          `Ubt-MeasureDetail-Approval-Request-Request`,
        );
    }
  }

  public setWithdrawButtonText(permission: string): string {
    return permission === PermissionContext.MeasureCreateApprovalRequest
      ? this.dataService.res("Ubt-MeasureDetail-Approval-Request-Withdraw")
      : this.dataService.res("Ubt-MeasureDetail-Approval-Request-Reject");
  }

  public formatAction(userName: string, role: string, action: string): string {
    let formattedAction = action;
    switch (action) {
      case StepDecision.APPROVED:
      case StepDecision.REJECTED:
        formattedAction = this.approvalRequestService.stepDecision.get(action);
        break;
      default:
        formattedAction = this.dataService.res(
          `Ubt-MeasureDetail-Approval-Request-${action}`,
        );
    }
    return userName + " (" + role + ") – " + formattedAction;
  }

  public getStatus(status: string): string {
    if (
      status === StepDecision.REJECTED ||
      status === RequestFormattedStatus.WITHDRAWN
    )
      return RequestFormattedStatus.WITHDRAWN;
    else return RequestFormattedStatus.SUBMITTED;
  }

  public addNewStep(
    steps: ApprovalStepViewModel[],
    status: string,
    reason: string,
    fullName: string,
    userRole: string,
    permission: string,
    maturityLevel: string,
    icon?: string,
    newLevel?: string,
  ): Observable<{ isActiveRequest: boolean; canWithdraw: boolean }> {
    const newMaturityLevel =
      newLevel ?? this.approvalRequestService.nextMaturityLevel(maturityLevel);
    const requestId = steps.find((step) => "Id" in step)?.Id;

    const newApprovalRequest = new ApprovalRequest({
      Description: reason,
      Status: status,
      RequestedDate: new Date(),
      Subject: `Change Maturity Level ${maturityLevel} -> ${newMaturityLevel} [${new Date()}]`,
    });
    const newApprovalStep = new ApprovalStep({
      Decision: status,
      DecisionDate: new Date(),
      DecisionReasoning:
        userRole === ROLES.Admin ? "Admin direct modification" : reason,
      SeqNr: steps[0]?.SeqNr ? steps[0].SeqNr + 1 : 1,
      ParentId: requestId,
      IdRefDecidedBy: localStorage.getItem("profileId"),
    });
    const action$: Observable<IPersistResult> = (() => {
      switch (permission) {
        case PermissionContext.MeasureChangeHartegrad:
          return this.changeMaturityLevel(
            newApprovalRequest,
            newApprovalStep,
            newMaturityLevel,
          );
        case PermissionContext.MeasureCreateApprovalRequest:
          return status === RequestStatus.IN_PROGRESS
            ? this.createRequest(
                newApprovalRequest,
                this.approvalProcessLibDesignation,
              )
            : this.withdrawRequest(newApprovalStep);
        case PermissionContext.MeasureApprovalRequestStep1:
          return this.approveOrRejectRequest(newApprovalStep, permission);
        case PermissionContext.MeasureApprovalRequestStep2:
          return this.approveOrRejectRequest(
            newApprovalStep,
            permission,
            newMaturityLevel,
          );
      }
    })();
    return action$.pipe(
      first(),
      switchMap((result) => {
        const adminWithdrawFlag = this.adminWithdraw;
        if (this.adminWithdraw) {
          steps.unshift({
            Status: RequestFormattedStatus.WITHDRAWN,
            Date: newApprovalRequest.RequestedDate,
            Reason: "Admin direct modification of maturity level",
            User: fullName,
            Role: userRole,
            Icon: ActionIcon.WITHDRAW,
          });
          this.adminWithdraw = false;
        }
        const newStep = {
          Id: result?.Id,
          Status: this.formatStatus(newApprovalStep.Decision, permission),
          Date: newApprovalRequest.RequestedDate,
          Reason: newApprovalRequest.Description,
          User: fullName,
          Role: userRole,
          Icon: icon ?? null,
          SeqNr: newApprovalStep.SeqNr,
        };
        steps.unshift(newStep);
        this.cdr.detectChanges();
        this.approvalRequestService.emitNewStep(newStep);
        const parentRequestId = result?.Id;
        const decisionOrStatus =
          permission === PermissionContext.MeasureCreateApprovalRequest
            ? newApprovalRequest.Status
            : newApprovalStep.Decision;
        return this.sendAprouvalNotification(
          permission,
          parentRequestId,
          decisionOrStatus,
          adminWithdrawFlag,
        );
      }),
      map((notificationResult) => ({
        isActiveRequest: this.approvalRequestService.verifyRecentRequest(
          steps[0],
        ),
        canWithdraw: this.approvalRequestService.verifyWithdrawStep(
          steps[0],
          permission,
        ),
      })),

      catchError((error) => {
        this.notificationService.callNotification({
          message: NotificationMessage.Error,
          type: ToastType.ERROR,
        });
        console.error(error);
        return EMPTY;
      }),
    );
  }
  private changeMaturityLevel(
    newApprovalRequest: ApprovalRequest,
    withdrawStep: ApprovalStep,
    newMaturityLevel: string,
  ): Observable<IPersistResult> {
    return this.approvalRequestService
      .getApprovalRequests(this.approvalRequestService.measureProgressId)
      .pipe(
        concatMap((approvalRequests) => {
          this.adminWithdraw =
            Array.isArray(approvalRequests) &&
            approvalRequests.length > 0 &&
            this.approvalRequestService.verifyRecentRequest(
              this.approvalRequestService.getFormattedSteps(
                approvalRequests,
              )[0],
            );
          return (
            this.adminWithdraw ? this.withdrawRequest(withdrawStep) : of(null)
          ).pipe(
            delay(1000),
            concatMap(() =>
              this.createRequest(
                newApprovalRequest,
                this.approvalProcessLibDesignation,
              ),
            ),
            concatMap(() =>
              this.updateMaturityLevel(
                this.approvalRequestService.measureProgressId,
                newMaturityLevel,
              ),
            ),
          );
        }),
      ) as unknown as Observable<IPersistResult>;
  }
  private updateMaturityLevel(
    measureProgressId: string,
    newMaturityLevel: string,
  ): Observable<[ArrayBuffer, ArrayBuffer]> {
    return forkJoin([
      this.dataService.persistObject(
        PersistMode.Update,
        ObjectKeys.MEASUREPROGRESS,
        [
          this.coreDataService.createPersistTargetColumn(
            "MaturityLevel",
            newMaturityLevel,
          ),
        ],
        measureProgressId,
      ),
      this.dataService
        .readObjects(
          ObjectKeys.MEASUREPROGRESS,
          [this.coreDataService.createQueryColumn("ParentId")],
          `Id='${measureProgressId}'`,
        )
        .pipe(
          concatMap((result) =>
            this.dataService.persistObject(
              PersistMode.Update,
              ObjectKeys.MEASURE,
              [
                this.coreDataService.createPersistTargetColumn(
                  "CurrentGrade",
                  newMaturityLevel,
                ),
              ],
              result[0].ParentId,
            ),
          ),
        ),
    ]);
  }
  private approveOrRejectRequest(
    newApprovalStep: ApprovalStep,
    permission: string,
    newMaturityLevel?: string,
  ): Observable<IPersistResult> {
    const ApprovalRequestColumns: TargetColumnValue[] = [
      this.coreDataService.createPersistTargetColumn(
        "Decision",
        newApprovalStep.Decision,
      ),
      this.coreDataService.createPersistTargetColumn(
        "DecisionDate",
        newApprovalStep.DecisionDate,
      ),
      this.coreDataService.createPersistTargetColumn(
        "DecisionReasoning",
        newApprovalStep.DecisionReasoning,
      ),
      this.coreDataService.createPersistTargetColumn(
        "SeqNr",
        newApprovalStep.SeqNr,
      ),
      this.coreDataService.createPersistTargetColumn(
        "ParentId",
        newApprovalStep.ParentId,
      ),
      this.coreDataService.createPersistTargetColumn(
        "IdRefDecidedBy",
        newApprovalStep.IdRefDecidedBy,
      ),
    ];
    const status =
      newApprovalStep.Decision === StepDecision.APPROVED
        ? permission == PermissionContext.MeasureApprovalRequestStep2
          ? RequestStatus.APPROVED
          : RequestStatus.IN_PROGRESS
        : RequestStatus.REJECTED;
    let persistResult;
    return this.dataService
      .persistObject(
        PersistMode.Insert,
        ObjectKeys.APPROVAL_STEP,
        ApprovalRequestColumns,
      )
      .pipe(
        tap((result) => {
          persistResult = result; // capture persistObject result
        }),
        concatMap(() => {
          return this.updateApprovalRequestStatus(
            newApprovalStep.ParentId,
            status,
          );
        }),
        concatMap(() => {
          return status === RequestStatus.APPROVED
            ? this.updateMaturityLevel(
                this.approvalRequestService.measureProgressId,
                newMaturityLevel,
              )
            : of(null);
        }),
        map(() => persistResult),
      ) as unknown as Observable<IPersistResult>;
  }
  private withdrawRequest(
    withdrawStep: ApprovalStep,
  ): Observable<IPersistResult> {
    const ApprovalRequestColumns: TargetColumnValue[] = [
      this.coreDataService.createPersistTargetColumn(
        "Decision",
        StepDecision.REJECTED,
      ),
      this.coreDataService.createPersistTargetColumn(
        "DecisionDate",
        withdrawStep.DecisionDate,
      ),
      this.coreDataService.createPersistTargetColumn(
        "DecisionReasoning",
        withdrawStep.DecisionReasoning,
      ),
      this.coreDataService.createPersistTargetColumn("SeqNr", "4"),
      this.coreDataService.createPersistTargetColumn(
        "ParentId",
        withdrawStep.ParentId,
      ),
      this.coreDataService.createPersistTargetColumn(
        "IdRefDecidedBy",
        withdrawStep.IdRefDecidedBy,
      ),
    ];
    return this.dataService
      .persistObject(
        PersistMode.Insert,
        ObjectKeys.APPROVAL_STEP,
        ApprovalRequestColumns,
      )
      .pipe(
        concatMap(() => {
          return this.updateApprovalRequestStatus(
            withdrawStep.ParentId,
            RequestStatus.REJECTED,
          );
        }),
      ) as unknown as Observable<IPersistResult>;
  }
  private updateApprovalRequestStatus(
    approvalRequestId: string,
    status: string,
  ): Observable<IPersistResult> {
    return this.dataService
      .persistObject(
        PersistMode.Update,
        ObjectKeys.APPROVAL_REQUEST,
        [this.coreDataService.createPersistTargetColumn("Status", status)],
        approvalRequestId,
      )
      .pipe(first()) as unknown as Observable<IPersistResult>;
  }
  private createRequest(
    newRequest: ApprovalRequest,
    approvalProcessLibDesignation: string,
  ): Observable<IPersistResult> {
    return this.getApprovalProcessLibId(approvalProcessLibDesignation).pipe(
      switchMap((approvalProcessLib) => {
        const ApprovalRequestColumns: TargetColumnValue[] = [
          this.coreDataService.createPersistTargetColumn(
            "Description",
            newRequest.Description,
          ),
          this.coreDataService.createPersistTargetColumn(
            "RequestedDate",
            newRequest.RequestedDate,
          ),
          this.coreDataService.createPersistTargetColumn(
            "Status",
            newRequest.Status,
          ),
          this.coreDataService.createPersistTargetColumn(
            "Subject",
            newRequest.Subject,
          ),
          this.coreDataService.createPersistTargetColumn(
            "IdRefApprovalProcessLib",
            approvalProcessLib.Id,
          ),
          this.coreDataService.createPersistTargetColumn(
            "IdRefRequestedBy",
            localStorage.getItem("profileId"),
          ),
          this.coreDataService.createPersistTargetColumn(
            "ParentId",
            this.approvalRequestService.measureProgressId,
          ),
          this.coreDataService.createPersistTargetColumn(
            "ParentObjectType",
            ObjectKeys.MEASUREPROGRESS,
          ),
        ];
        return this.dataService.persistObject(
          PersistMode.Insert,
          ObjectKeys.APPROVAL_REQUEST,
          ApprovalRequestColumns,
        ) as unknown as Observable<IPersistResult>;
      }),
      first(),
    );
  }
  private getApprovalProcessLibId(
    approvalProcessLibDesignation: string,
  ): Observable<Partial<ApprovalProcessLib>> {
    return this.dataService
      .readObjects(
        ObjectKeys.APPROVAL_PROCESS_LIB,
        [this.coreDataService.createQueryColumn("Id")],
        `Designation='${approvalProcessLibDesignation}'`,
      )
      .pipe(
        first(),
        map((result) => result[0]),
      );
  }
  public getFullName(): Observable<Partial<PersonViewModel>[]> {
    return this.dataService.readObjects(
      ObjectKeys.PERSON,
      [this.coreDataService.createQueryColumn("ref:Id", "fullName")],
      `Id='${localStorage.getItem("profileId")}' AND ParentId='${sessionStorage.getItem("profileAddressId")}'`,
    );
  }
  private formatStatus(status: string, permission: string): string {
    switch (permission) {
      case PermissionContext.MeasureCreateApprovalRequest:
        return status === RequestStatus.IN_PROGRESS
          ? RequestFormattedStatus.SUBMITTED
          : RequestFormattedStatus.WITHDRAWN;
      case PermissionContext.MeasureChangeHartegrad:
        return status === RequestStatus.APPROVED
          ? RequestFormattedStatus.MODIFIED
          : RequestFormattedStatus.WITHDRAWN;
      default:
        return status;
    }
  }

  private sendAprouvalNotification(
    permission: string,
    requestId: string,
    decision?: string,
    adminWithdraw?: boolean,
  ): Observable<any> {
    const redirectUrl = this.dataService.generateRedirectionUrl(
      AIMSROUTES.MEASURE_DETAIL,
      [AIMSROUTES.MEASURES, this.measureService.currentMeasure().Id],
    );

    return this.getConnectedUserName$().pipe(
      switchMap((userConnected) => {
        const measureName =
          this.measureService.currentMeasure().Designation ??
          this.measureService.currentMeasure().Title;

        switch (permission) {
          /* ---------------- CREATE REQUEST ---------------- */
          case PermissionContext.MeasureCreateApprovalRequest:
            if (decision !== RequestStatus.IN_PROGRESS) return of(null);

            return this.dataService
              .loadUserByPermessionList(`${Permission.APPROVAL_REQUEST_STEP1}`)
              .pipe(
                switchMap((users) => {
                  if (!users?.length) return of(null);

                  const inAppIds: string[] = Array.from(
                    new Set(
                      users.filter((u) => u.InAppNotification).map((u) => u.Id),
                    ),
                  );

                  const emailIds = Array.from(
                    new Set(
                      users.filter((u) => u.EmailNotification).map((u) => u.Id),
                    ),
                  );

                  return forkJoin([
                    this.createInAppNotification(
                      requestId,
                      "notification.Measure-ApprovalRequest-NewRequestSubmitted",
                      inAppIds,
                      330001202,
                    ),
                    this.sendEmailsToUsers(
                      emailIds,
                      EmailNotificationSubjects.MEASURE_NEW_APPROUVAL_REQUEST_SUBMITTED,
                      EmailNotificationDescriptions.MEASURE_NEW_APPROUVAL_REQUEST_SUBMITTED,
                      redirectUrl,
                      { 0: measureName, 1: userConnected },
                    ),
                  ]);
                }),
              );

          /* ---------------- STEP 1 ---------------- */
          case PermissionContext.MeasureApprovalRequestStep1:
            return this.handleStepDecision(
              requestId,
              decision,
              measureName,
              redirectUrl,
              userConnected,
              Permission.APPROVAL_REQUEST_STEP2,
            );

          /* ---------------- STEP 2 ---------------- */
          case PermissionContext.MeasureApprovalRequestStep2:
            return this.handleFinalDecision(
              requestId,
              decision,
              measureName,
              redirectUrl,
              userConnected,
            );

          /* ---------------- ADMIN WITHDRAW ---------------- */
          case PermissionContext.MeasureChangeHartegrad:
            if (!adminWithdraw) return of(null);

            return this.getApprovalRequester(requestId).pipe(
              switchMap((requesters) => {
                const inAppIds: string[] = Array.from(
                  new Set(
                    requesters
                      .filter((r) => r.InAppNotification)
                      .map((r) => r.IdRefRequestedBy),
                  ),
                );

                const emailIds: string[] = Array.from(
                  new Set(
                    requesters
                      .filter((r) => r.EmailNotification)
                      .map((r) => r.IdRefRequestedBy),
                  ),
                );

                return forkJoin([
                  this.createInAppNotification(
                    requestId,
                    "notification.Measure-ApprovalRequest-RequestWithdrawnByAdmin",
                    inAppIds,
                  ),
                  this.sendEmailsToUsers(
                    emailIds,
                    EmailNotificationSubjects.MEASURE_APPROUVAL_REQUEST_UPDATE,
                    EmailNotificationDescriptions.MEASURE_APPROUVAL_REQUEST_UPDATE,
                    redirectUrl,
                    { 0: measureName },
                  ),
                ]);
              }),
            );

          default:
            return of(null);
        }
      }),
    );
  }

  private handleStepDecision(
    requestId: string,
    decision: string,
    measureName: string,
    redirectUrl: string,
    userConnected: string,
    nextPermission: Permission,
  ): Observable<any> {
    return this.getApprovalRequester(requestId).pipe(
      switchMap((requesters) => {
        if (!requesters?.length) return of(null);
        const requesterNames = requesters[0].fullName;
        const isApproved = decision === StepDecision.APPROVED;
        const notifications: Observable<any>[] = [];

        /* ---------------- IN-APP TO REQUESTER ---------------- */
        if (requesters.some((r) => r.InAppNotification === 1)) {
          notifications.push(
            this.createInAppNotification(
              requestId,
              isApproved
                ? "notification.Measure-ApprovalRequest-RequestApprouvedBy"
                : "notification.Measure-ApprovalRequest-RequestRejectedBy",
              requesters.map((r) => r.IdRefRequestedBy),
            ),
          );
        }

        /* ---------------- EMAIL TO REQUESTER ---------------- */
        const requesterEmailIds = requesters
          .filter((r) => r.EmailNotification === 1)
          .map((r) => r.IdRefRequestedBy);

        const requesterEmail$ = requesterEmailIds.length
          ? this.sendEmailsToUsers(
              requesterEmailIds,
              EmailNotificationSubjects.MEASURE_APPROUVAL_REQUEST_UPDATE,
              isApproved
                ? EmailNotificationDescriptions.MEASURE_APPROUVAL_REQUEST_APPROVED
                : EmailNotificationDescriptions.MEASURE_APPROUVAL_REQUEST_REJECTED,

              redirectUrl,
              {
                0: measureName,
                1: userConnected,
                2: this.dataService.getRole()[0],
              },
            )
          : of(null);

        /* ---------------- IF APPROVED → STEP 2 USERS ---------------- */
        if (!isApproved) {
          return forkJoin([
            notifications.length ? forkJoin(notifications) : of(null),
            requesterEmail$,
          ]);
        }

        const step2$ = this.dataService
          .loadUserByPermessionList(nextPermission)
          .pipe(
            switchMap((users) => {
              if (!users?.length) return of(null);

              const inAppIds: string[] = Array.from(
                new Set(
                  users
                    .filter((u) => u.InAppNotification === 1)
                    .map((u) => u.Id),
                ),
              );

              const emailIds: string[] = Array.from(
                new Set(
                  users
                    .filter((u) => u.EmailNotification === 1)
                    .map((u) => u.Id),
                ),
              );

              return forkJoin([
                this.createInAppNotification(
                  requestId,
                  "notification.Measure-ApprovalRequest-NewRequestSubmitted",
                  inAppIds,
                ),
                this.sendEmailsToUsers(
                  emailIds,
                  EmailNotificationSubjects.MEASURE_NEW_APPROUVAL_REQUEST_SUBMITTED,
                  EmailNotificationDescriptions.MEASURE_NEW_APPROUVAL_REQUEST_SUBMITTED,
                  redirectUrl,
                  { 0: measureName, 1: requesterNames },
                ),
              ]);
            }),
          );

        return forkJoin([
          notifications.length ? forkJoin(notifications) : of(null),
          requesterEmail$,
          step2$,
        ]);
      }),
    );
  }

  private handleFinalDecision(
    requestId: string,
    decision: string,
    measureName: string,
    redirectUrl: string,
    userConnected: string,
  ): Observable<any> {
    return this.getApprovalRequester(requestId).pipe(
      switchMap((requesters) => {
        if (!requesters?.length) return of(null);

        const isApproved = decision === StepDecision.APPROVED;

        const comment = isApproved
          ? "notification.Measure-ApprovalRequest-RequestApprouvedBy"
          : "notification.Measure-ApprovalRequest-RequestRejectedBy";

        const inAppIds = requesters
          .filter((r) => r.InAppNotification === 1)
          .map((r) => r.IdRefRequestedBy);

        const emailIds = requesters
          .filter((r) => r.EmailNotification === 1)
          .map((r) => r.IdRefRequestedBy);

        return forkJoin([
          this.createInAppNotification(requestId, comment, inAppIds),
          this.sendEmailsToUsers(
            emailIds,
            EmailNotificationSubjects.MEASURE_APPROUVAL_REQUEST_UPDATE,
            isApproved
              ? EmailNotificationDescriptions.MEASURE_APPROUVAL_REQUEST_APPROVED
              : EmailNotificationDescriptions.MEASURE_APPROUVAL_REQUEST_REJECTED,
            redirectUrl,
            {
              0: measureName,
              1: userConnected,
              2: this.dataService.getRole()[0],
            },
          ),
        ]);
      }),
    );
  }

  private getConnectedUserName$(): Observable<string> {
    return this.appService.loggedInUserInformations$.pipe(
      first(),
      map((user) => this.dataService.normalizeEmailText(user.fullName)),
    );
  }

  private createInAppNotification(
    requestId: string,
    comment: string,
    userIds: string[],
    parentType = 330001203,
  ): Observable<any> {
    if (!userIds.length) return of(null);

    return this.notificationService1.createNotifications([
      {
        ParentId: requestId,
        ParentObjectType: parentType,
        Comment: comment,
        Text: "",
        UserIdList: userIds,
      },
    ]);
  }

  private sendEmailsToUsers(
    userIds: string[],
    subjectKey: EmailNotificationSubjects,
    descriptionKey: EmailNotificationDescriptions,
    redirectUrl: string,
    params?: Record<string, any>, // 👈 optional
  ): Observable<any> {
    if (!userIds.length) return of(null);

    return this.dataService
      .readObjects(
        ObjectKeys.PERSON,
        [
          this.coreDataService.createQueryColumn("Id", "Id"),
          this.coreDataService.createQueryColumn("EMail", "EMail"),
          this.coreDataService.createQueryColumn(
            `Properties[Designation = '${NotificationSettingType.EMAIL}'].TextValue`,
            "Language",
          ),
         this.coreDataService.createQueryColumn(
         `Properties[Designation = '${NotificationSettingType.EMAIL}'].BoolValue`,
          "enabled",),
        ],
        `Id In ('${userIds.join("','")}')`,
      )
      .pipe(
        switchMap((persons: any[]) => {
          const validPersons =persons?.filter( (person) =>person.EMail &&Boolean(person.enabled), ) ?? [];

          return forkJoin(
            validPersons.map((person) => {
              const language = person.Language || "de";

              return from(
                this.languageService.translateBulk(
                  [
                    { field: "subject", key: subjectKey },
                    { field: "description", key: descriptionKey, params }, // params may be undefined
                    {
                      field: "notificationType",
                      key: EmailNotificationType.MEASURE_APPROUVAL_REQUEST_UPDATE,
                    },
                  ],
                  language,
                ),
              ).pipe(
                switchMap((translations) => {
                  const content = {
                    notificationType: translations.notificationType,
                    description: translations.description,
                    redirectUrl,
                  };

                  return this.notificationService1.sendNotificationEmail(
                    content,
                    translations.subject,
                    person.EMail,
                    language,
                  );
                }),
              );
            }),
          );
        }),
      );
  }
  private getApprovalRequester(requestId: string): Observable<
    {
      IdRefRequestedBy: string;
      fullName: string;
      EmailNotification: number;
      InAppNotification: number;
    }[]
  > {
    return this.dataService
      .readObjects(
        ObjectKeys.APPROVAL_STEP,
        [
          this.coreDataService.createQueryColumn(
            "ApprovalRequest.IdRefRequestedBy",
            "IdRefRequestedBy",
          ),
          this.coreDataService.createQueryColumn(
            "ref:ApprovalRequest.IdRefRequestedBy",
            "fullName",
          ),
          this.coreDataService.createQueryColumn(
            `iif(Exists(ApprovalRequest.Person.Properties[Designation = '${NotificationSettingType.EMAIL}']),ApprovalRequest.Person.Properties[Designation = '${NotificationSettingType.EMAIL}'].BoolValue <>   false, true)`,
            "EmailNotification",
          ),
          this.coreDataService.createQueryColumn(
            `iif(Exists(ApprovalRequest.Person.Properties[Designation = '${NotificationSettingType.INAPP}']),ApprovalRequest.Person.Properties[Designation = '${NotificationSettingType.INAPP}'].BoolValue <>   false, true)`,
            "InAppNotification",
          ),
        ],
        `Id='${requestId}'`,
      )
      .pipe(first());
  }
}
